// src/useKeyboardNavigation/useKeyboardNavigation.ts
import { useCallback, useImperativeHandle } from "react";
import { createDescendantsIndexContext } from "@volvo-cars/react-descendants";
function getControlsArguments({
  getByIndex,
  index,
  total
}) {
  const nextRef = getByIndex(index + 1);
  const prevRef = getByIndex(index - 1);
  const firstRef = getByIndex(0);
  const lastRef = getByIndex(total - 1);
  return {
    next: (options = {}) => {
      return {
        target: options.loop ? nextRef || firstRef : nextRef,
        options,
        type: "next"
      };
    },
    prev: (options = {}) => {
      return {
        target: options.loop ? prevRef || lastRef : prevRef,
        options,
        type: "prev"
      };
    },
    first: () => {
      return { target: firstRef, type: "first" };
    },
    last: () => {
      return { target: lastRef, type: "last" };
    }
  };
}
function createKeyboardNavigation() {
  const { useDescendantIndex, DescendantsIndexProvider } = createDescendantsIndexContext();
  function useAdvancedKeyboardNavigation(controls, { ref, onKeyDown } = {}) {
    const {
      ref: descendantRef,
      index,
      total,
      getByIndex
    } = useDescendantIndex();
    useImperativeHandle(ref, () => descendantRef.current);
    return {
      ref: descendantRef,
      onKeyDown: useCallback((event) => {
        var _a, _b, _c, _d;
        onKeyDown == null ? void 0 : onKeyDown(event);
        if (!getByIndex || typeof index !== "number" || typeof total !== "number") {
          return;
        }
        const controlsArgs = getControlsArguments({
          getByIndex,
          index,
          total
        });
        const eventKeys = controls(controlsArgs);
        const result = eventKeys[event.key];
        if ((_a = result == null ? void 0 : result.target) == null ? void 0 : _a.ref) {
          event.preventDefault();
          (_d = (_c = (_b = result.target) == null ? void 0 : _b.ref) == null ? void 0 : _c.current) == null ? void 0 : _d.focus();
        }
      }, [getByIndex, index, total, controls, onKeyDown])
    };
  }
  function useKeyboardNavigation({
    orientation = "horizontal",
    direction,
    ref,
    onKeyDown
  } = {}) {
    const props = useAdvancedKeyboardNavigation(({ next, prev, first, last }) => {
      var _a;
      const firstTarget = first();
      const firstElement = (_a = firstTarget == null ? void 0 : firstTarget.target) == null ? void 0 : _a.ref.current;
      const isVertical = orientation === "vertical";
      const dir = direction || (firstElement && !isVertical ? getComputedStyle(firstElement).direction || "ltr" : "ltr");
      const isRtl = dir === "rtl";
      return {
        [isVertical ? "ArrowDown" : isRtl ? "ArrowLeft" : "ArrowRight"]: next({
          loop: true
        }),
        [isVertical ? "ArrowUp" : isRtl ? "ArrowRight" : "ArrowLeft"]: prev({
          loop: true
        }),
        Home: firstTarget,
        End: last()
      };
    }, { ref, onKeyDown });
    return props;
  }
  return {
    KeyboardNavigationProvider: DescendantsIndexProvider,
    useAdvancedKeyboardNavigation,
    useKeyboardNavigation
  };
}
export {
  createKeyboardNavigation
};
